import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Text, TouchableOpacity, View} from 'react-native';
import {Popup} from 'react-native-popup-confirm-toast';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {setAccessToken, setUserInfo} from '../helpers/storage';
import {persistLastUser} from '../helpers/lastUserCache';
import {persistRestaurant} from '../helpers/restaurantCache';
import {safeSetJson} from '../helpers/storageQuota';
import {useNavigation} from '@react-navigation/native';
import {Icon} from "react-native-elements";
import Observables from "@happy-gastro/global-observables";
import {useTranslation} from "react-i18next";
import {getAppModeForUser, MANAGER_ROLES, hasAnyRole, hasAdminRole, hasHqStoreMembership, getAvailableAppModesForUser} from '../helpers/managerRoles';
import {getRouteForAppMode, resolveModeForAppVariant} from '../config/appVariant';
import {getSetting, removeSetting} from '../helpers/persistentSettings';
import NoLicenseModal from './NoLicenseModal';

const RestaurantPlaceChooser = ({ data, onDismiss }) => {
    const navigation = useNavigation();
    const { t } = useTranslation();

    // Ensures the chooser only resolves once (auto-select, picker, or the
    // no-license gate) even if the effect re-runs because i18n / data
    // references change mid-mount.
    const handledRef = useRef(false);

    // When the authenticated user has no valid business to enter (no restaurant,
    // or the restaurant has no place) we never persist a session — we show the
    // premium "no active license" gate instead of the business picker.
    const [noLicense, setNoLicense] = useState(false);

    const isManagerUser = useCallback((place) => hasAnyRole(data.user, place, MANAGER_ROLES), [data.user]);

    // const SERVICE_ROLES = ['serviceAdmin', 'serviceTechnician', 'serviceAdvisor', 'serviceViewer', 'serviceExternal'];
    // const STOCK_ROLES = ['stock:manager'];

    // const isServiceUser = (user, place) => {
    //     // Check array-based role fields
    //     const rolesArray = user?.roles || user?.userRoles || [];
    //     if (rolesArray.some(role => SERVICE_ROLES.includes(role))) return true;

    //     // Check singular role field on user
    //     if (user?.role && SERVICE_ROLES.includes(user.role)) return true;

    //     // Check place-level role
    //     if (place?.role && SERVICE_ROLES.includes(place.role)) return true;

    //     // Check restaurant-level role
    //     const restaurant = user?.restaurants?.[0];
    //     if (restaurant?.role && SERVICE_ROLES.includes(restaurant.role)) return true;

    //     return false;
    // };

    // const isStockUser = (user, place) => {
    //     // Check array-based role fields
    //     const rolesArray = user?.roles || user?.userRoles || [];
    //     if (rolesArray.some(role => STOCK_ROLES.includes(role))) return true;

    //     // Check singular role field on user
    //     if (user?.role && STOCK_ROLES.includes(user.role)) return true;

    //     // Check place-level role
    //     if (place?.role && STOCK_ROLES.includes(place.role)) return true;

    //     // Check restaurant-level role
    //     const restaurant = user?.restaurants?.[0];
    //     if (restaurant?.role && STOCK_ROLES.includes(restaurant.role)) return true;

    //     return false;
    // };    

    const handlePlaceSelection = useCallback(async (place) => {
        await setAccessToken(data.token);

        await setUserInfo(data.user);
        // Slim projection + guarded write: the raw user payload crossed browser
        // localStorage quota on bloated accounts (Sentry HAPPY-APP-1D).
        await persistLastUser(data.user);
        // Slim projection + quota-guarded write. The raw store document carries
        // five unbounded credit ledgers and stored HTML templates, which crossed
        // the browser localStorage quota on long-lived stores (Sentry: "Setting
        // the value of 'restaurant' exceeded the quota") and cost a multi-MB
        // serialization on every low-RAM terminal. See helpers/restaurantCache.
        await persistRestaurant(data.user?.restaurants?.[0] ?? null);

        await safeSetJson('restaurantPlace', place, { label: 'restaurantPlace' });

        // Determine the app mode to land on. Honour a previously chosen & persisted
        // module first: if the user already picked another app (saved to the device)
        // and it's still allowed by their roles, keep it. Otherwise fall back to the
        // role-based default (any admin role -> Management; service/stock roles ->
        // their own app, so they never get the Sales-only "Day opening" dialog).
        const storedAppMode = await getSetting('userAppMode');
        const allowedModes = getAvailableAppModesForUser(data.user, place);
        const roleDefaultMode = getAppModeForUser(data.user, place);
        // Admin-style roles (system:admin, restaurantStaff:admin, sales:admin, …) MUST
        // always land on the Management app on a fresh credential login. `userAppMode`
        // is a DEVICE-GLOBAL setting that survives logout (performLogout only clears the
        // token + user info) and is even re-persisted as 'restaurant' on logged-out cold
        // boots, so a previous POS/Sales user on this device — or the admin's own earlier
        // in-session switch — could otherwise drag a freshly logging-in admin straight
        // into the POS HomeScreen. For admins we therefore ignore any stale persisted
        // mode here and force their role default (Management). The in-app module switcher
        // still lets them move to POS/Service/Warehouse within the session, and that
        // choice is honoured on restart (App.jsx loadRuntimeState) — it's only the
        // login event that is pinned to Management.
        // Members of the Happy Solutions head-office store are pinned the same way:
        // they now default to Management (`getDefaultOperationalModeForUser`), so a
        // stale device-global 'restaurant' left behind by a previous POS user on the
        // same terminal must not drag an internal colleague into the till on login.
        const isAdminUser = hasAdminRole(data.user, place) || hasHqStoreMembership(data.user, place);
        const preferredMode = !isAdminUser && allowedModes.includes(storedAppMode)
            ? storedAppMode
            : roleDefaultMode;
        const appMode = resolveModeForAppVariant(preferredMode);
        const serviceUser = appMode === 'service';
        const stockUser = appMode === 'stock';
        console.log(
            '[RestaurantPlaceChooser] App mode:',
            appMode,
            'Service user:',
            serviceUser,
            'Stock user:',
            stockUser,
            'User role:',
            data.user?.role,
            'Roles:',
            data.user?.roles,
            'Place role:',
            place?.role
        );
        await AsyncStorage.setItem('userAppMode', appMode);

        // A fresh credential login into Management always opens on the first
        // (Manager) bottom tab. Drop the remembered last-used tab so a prior
        // session (e.g. left on Settings) never re-lands the user there on login.
        // A user-pinned "Main screen" (setHomeTab / home-landing picker) still
        // wins — only the last-used-tab memory is reset; normal app reopens
        // (no login) keep restoring the last tab.
        if (appMode === 'manager') {
            await removeSetting('lastTab:management');
        }

        globalThis.setTimeout(() => Observables.call('fetchSystemData'), 1000);

        Popup.hide();

        // Only the Sales app uses the Day opening flow; every other module jumps
        // straight to its own home screen.
        const targetRoute = appMode === 'restaurant' ? null : getRouteForAppMode(appMode);

        // Defer the post-login navigation one beat past Popup.hide().
        //
        // The business picker is a full-screen `react-native-popup-confirm-toast`
        // overlay that ONLY unmounts inside its close-animation completion callback.
        // Firing navigation.reset()/navigate() synchronously here kicks off a full
        // navigator-key remount (App.jsx re-resolves the app-mode flags → the whole
        // Stack.Navigator remounts) plus a fetchSystemData burst in the same frame;
        // on iOS that drops the popup's completion callback, so the overlay stays
        // mounted at opacity 0 — invisible but still capturing EVERY touch. The app
        // then looks correct yet nothing taps or scrolls until it is restarted
        // (a fresh mount has no stuck overlay). Deferring lets the popup fully tear
        // down before the remount storm; paired with `useNativeDriver: false` on the
        // picker so the teardown is JS-scheduled and can never be dropped.
        globalThis.setTimeout(() => {
            if (targetRoute) {
                navigation.reset({
                    index: 0,
                    routes: [{ name: targetRoute, params: { loggedIn: true } }],
                });
            } else {
                navigation.navigate(getRouteForAppMode(appMode), { loggedIn: true });
            }
        }, 350);
    }, [data, navigation]);

    const bodyComponent = useCallback(({ bodyProps }) => (
        <View
            style={{
                padding: 20,
                width: '100%',
                backgroundColor: 'white',
                borderRadius: 15,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.2,
                shadowRadius: 5,
                elevation: 5,
            }}
            onLayout={(e) => {
                if (bodyProps.onLayout) bodyProps.onLayout(e);
            }}
        >
            <Text
                style={{
                    fontSize: 18,
                    fontWeight: 'bold',
                    marginBottom: 20,
                    textAlign: 'center',
                }}
            >
                {t("select_business")}
            </Text>
            {isManagerUser() && (
                <TouchableOpacity
                    style={{
                        backgroundColor: '#111827',
                        borderRadius: 10,
                        padding: 18,
                        marginBottom: 15,
                        alignItems: 'center',
                        shadowColor: '#000',
                        shadowOffset: { width: 1, height: 1 },
                        shadowOpacity: 0.2,
                        shadowRadius: 3,
                        elevation: 2,
                    }}
                    onPress={() => handlePlaceSelection({
                        _id: '__all__',
                        name: t('management.show_all_places') || 'Összes üzlet',
                        raw: 'All places',
                        managerAllPlaces: true,
                    })}
                    activeOpacity={0.7}
                >
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Icon name="apps" size={30} color="#fff" style={{ marginRight: 10 }} />
                        <Text style={{ color: '#fff', fontSize: 16, fontWeight: '700' }}>
                            {t('management.show_all_places') || 'Összes üzlet'}
                        </Text>
                    </View>
                </TouchableOpacity>
            )}
            {(Array.isArray(data?.user?.restaurants?.[0]?.places) ? data.user.restaurants[0].places : []).map((place, index) => (
                <TouchableOpacity
                    key={index}
                    style={{
                        backgroundColor: '#ffedd5',
                        borderRadius: 10,
                        padding: 18,
                        marginBottom: 15,
                        alignItems: 'center',
                        shadowColor: '#000',
                        shadowOffset: { width: 1, height: 1 },
                        shadowOpacity: 0.2,
                        shadowRadius: 3,
                        elevation: 2,
                    }}
                    onPress={() => handlePlaceSelection(place)}
                    activeOpacity={0.7}
                >
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Icon name="store" size={30} color="#333" style={{ marginRight: 10 }} />
                        <Text style={{ color: '#333', fontSize: 16, fontWeight: '600' }}>
                            {place.name || place.raw}
                        </Text>
                    </View>
                </TouchableOpacity>
            ))}
        </View>
    ), [data?.user, handlePlaceSelection, isManagerUser, t]);

    useEffect(() => {
        if (handledRef.current) return;

        const restaurants = Array.isArray(data?.user?.restaurants) ? data.user.restaurants : [];
        const places = Array.isArray(restaurants[0]?.places) ? restaurants[0].places : [];

        // No valid restaurant or restaurant place → the user holds no active
        // license for this app. Block entry (no token/user is ever persisted —
        // that only happens in handlePlaceSelection) and show the license gate.
        if (restaurants.length === 0 || places.length === 0) {
            handledRef.current = true;
            setNoLicense(true);
            return;
        }

        // User-friendly shortcut (mirrors the admin panel): when the user belongs to
        // exactly one restaurant that has exactly one place, there is nothing to choose —
        // auto-select it and skip the business chooser popup entirely.
        if (restaurants.length === 1 && places.length === 1) {
            handledRef.current = true;
            handlePlaceSelection(places[0]);
            return;
        }

        handledRef.current = true;
        Popup.show({
            type: 'confirm',
            // JS-driven (NOT the native driver): the popup only unmounts from its
            // close-animation completion callback, and a native-driver callback is
            // silently dropped on iOS when Popup.hide() collides with the post-login
            // navigator remount — leaving an invisible full-screen overlay that
            // blocks every touch. A JS-scheduled callback always fires. See
            // handlePlaceSelection for the full write-up.
            useNativeDriver: false,
            label: t('business_selection'),
            textBody: t('choose_business_to_continue'),
            bodyComponentForce: true,
            bodyComponent: (bodyProps) => bodyComponent({ bodyProps }),
            iconEnabled: true,
            buttonEnabled: false,
        });
    }, [bodyComponent, t, data?.user, handlePlaceSelection]);

    const closeNoLicense = useCallback(() => {
        setNoLicense(false);
        // Hand control back to the login screen so it can clear the pending
        // session and let the user try a different account.
        onDismiss?.();
    }, [onDismiss]);

    return <NoLicenseModal visible={noLicense} onClose={closeNoLicense} t={t} />;
};

export default RestaurantPlaceChooser;
